import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { 
  FaExpand,             // Fullscreen
  FaCompress,           // FullscreenExit
  FaRegWindowRestore,   // PictureInPicture
  FaCog,                // Settings
  FaQuestionCircle,     // HelpOutline
  FaExclamationCircle,                // Report
  FaFlag,               // Flag
  FaTint,              // BlurOn
  FaTh,                 // GridView
  FaColumns,            // ViewSidebar
  FaRegDotCircle,       // CenterFocusStrong
  FaThLarge,            // Apps
  FaVideo,              // Videocam
  FaVideoSlash,         // VideocamOff
  FaMicrophone,         // Mic
  FaMicrophoneSlash     // MicOff
} from 'react-icons/fa';
import { MdAutoAwesome } from 'react-icons/md';
import { showBackgoundImageEffectStatus } from '../../store/slices/roomSlice';
import { RECORDING_UI_ENABLED } from '../../config/meetingFeatureFlags';

const ShowMore = ({ 
  onClose, 
  onFullScreen, 
  onPiP, 
  onReportProblem, 
  onReportAbuse, 
  onHelp,
  onSettings,
  onAdjustView,
  onBackgroundEffects,
  initialRecordingAllowed = false,
  initialBackground = null,
  initialView = 'default',
}) => {
  const [recordingAllowed, setRecordingAllowed] = useState(initialRecordingAllowed);
  const dispatch = useDispatch();
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [pipAvailable, setPipAvailable] = useState(false);
  const [showBackgroundEffects, setShowBackgroundEffects] = useState(false);
  const [showViewOptions, setShowViewOptions] = useState(false);
  const [selectedBackground, setSelectedBackground] = useState(initialBackground);
  const [selectedView, setSelectedView] = useState(initialView);
  const { showBackgoundImageEffect } = useSelector((state) => state.room);
 
  const menuRef = useRef(null);
  const viewOptionsRef = useRef(null);
  const bgOptionsRef = useRef(null);

  // View options with icons
  const viewOptions = [
    { id: 'default', name: 'Default view', icon: <FaTh fontSize="small" /> },
    { id: 'sidebar', name: 'Sidebar view', icon: <FaColumns fontSize="small" /> },
    { id: 'spotlight', name: 'Spotlight speaker', icon: <FaRegDotCircle fontSize="small" /> },
    { id: 'tiled', name: 'Tiled view', icon: <FaThLarge fontSize="small" /> },
  ];

  // Background options
  const backgrounds = [
    { id: 'blur', name: 'Blur', icon: <FaTint fontSize="small" /> },
    { id: 'office', name: 'Office', icon: <img src="/office-icon.png" alt="Office" className="w-4 h-4" /> },
    { id: 'beach', name: 'Beach', icon: <img src="/beach-icon.png" alt="Beach" className="w-4 h-4" /> },
    { id: 'none', name: 'None', icon: <FaVideo fontSize="small" /> },
  ];

  useEffect(() => {
    setPipAvailable('pictureInPictureEnabled' in document);
    const handleFullScreenChange = () => {
      setIsFullScreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', handleFullScreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullScreenChange);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        onClose?.();
      }
      if (showViewOptions && viewOptionsRef.current && 
          !viewOptionsRef.current.contains(event.target) && 
          !event.target.closest('.view-options-trigger')) {
        setShowViewOptions(false);
      }
      if (showBackgroundEffects && bgOptionsRef.current && 
          !bgOptionsRef.current.contains(event.target) && 
          !event.target.closest('.bg-options-trigger')) {
        setShowBackgroundEffects(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose, showViewOptions, showBackgroundEffects]);

  const handleBackgroundSelect = (bgId) => {
    setSelectedBackground(bgId);
    setShowBackgroundEffects(false);
    onBackgroundEffects?.(bgId);
  };

  const handleViewSelect = (viewId) => {
    setSelectedView(viewId);
    setShowViewOptions(false);
    onAdjustView?.(viewId);
  };

  return (
    <div 
      ref={menuRef}
      className="meeting-more-menu fixed bottom-20 left-1/2 transform -translate-x-1/2 w-72 bg-[#202124] text-white rounded-lg shadow-xl z-50 overflow-hidden border border-gray-700"
    >
      {RECORDING_UI_ENABLED && (
        <div className="px-4 py-3 text-sm border-b border-gray-700">
          <p className="font-medium flex items-center">
            {recordingAllowed ? (
              <span className="w-3 h-3 bg-red-500 rounded-full mr-2 animate-pulse"></span>
            ) : (
              <span className="w-3 h-3 bg-gray-500 rounded-full mr-2"></span>
            )}
            {recordingAllowed ? "Recording in progress" : "Recording not available"}
          </p>
          <p className="text-xs text-gray-400 pl-5">
            {recordingAllowed
              ? "This meeting is being recorded"
              : "Recording hasn't started"}
          </p>
        </div>
      )}

      {/* Adjust view option */}
      <div className="relative">
        <button 
          className="w-full text-left px-4 py-2 text-sm hover:bg-[#3c4043] flex items-center view-options-trigger"
          aria-expanded={showViewOptions}
          onClick={() => {
            setShowViewOptions(!showViewOptions);
            setShowBackgroundEffects(false);
          }}
        >
          <FaTh className="mr-3 text-gray-400" fontSize="small" />
          <span>Adjust view</span>
          {/* <span className={`ml-auto transform transition-transform ${showViewOptions ? 'rotate-90' : ''}`}>▶</span> */}
        </button>
        
        {showViewOptions && (
          <div 
            ref={viewOptionsRef}
            className="relative mt-1 w-full bg-[#202124] rounded-lg shadow-lg border border-gray-700 z-10"
          >
            {viewOptions.map((view) => (
              <button
                key={view.id}
                className={`w-full text-left px-4 py-2 text-sm hover:bg-[#3c4043] flex items-center ${
                  selectedView === view.id ? 'bg-[#3c4043] font-medium' : ''
                }`}
                onClick={() => handleViewSelect(view.id)}
              >
                <span className="mr-3 text-gray-400">{view.icon}</span>
                {view.name}
              </button>
            ))}
          </div>
        )}
      </div>
      
      {/* Full screen */}
      <button 
        className="w-full text-left px-4 py-2 text-sm hover:bg-[#3c4043] flex items-center"
        onClick={onFullScreen}
      >
        {isFullScreen ? (
          <FaCompress className="mr-3 text-gray-400" fontSize="small" />
        ) : (
          <FaExpand className="mr-3 text-gray-400" fontSize="small" />
        )}
        {isFullScreen ? "Exit full screen" : "Full screen"}
      </button>
      
      {/* Picture-in-picture */}
      {pipAvailable && (
        <button 
          className="w-full text-left px-4 py-2 text-sm hover:bg-[#3c4043] flex items-center"
          onClick={onPiP}
        >
          <FaRegWindowRestore className="mr-3 text-gray-400" fontSize="small" />
          Open picture-in-picture
        </button>
      )}
      
      {/* Backgrounds and effects */}
      <div className="relative">
        <button 
          className="w-full text-left px-4 py-2 text-sm hover:bg-[#3c4043] flex items-center bg-options-trigger"
          aria-expanded={showBackgroundEffects}
          onClick={() => {
            setShowBackgroundEffects(!showBackgroundEffects);
            setShowViewOptions(false);
            dispatch(showBackgoundImageEffectStatus(!showBackgoundImageEffect));
          }}
        >
          <MdAutoAwesome className="mr-3 text-gray-400" fontSize="small" />
          <span>Backgrounds and effects</span>
          {/* <span className={`ml-auto transform transition-transform ${showBackgroundEffects ? 'rotate-90' : ''}`}>▶</span> */}
        </button>
        
        {showBackgroundEffects && (
          <div 
            ref={bgOptionsRef}
            className="relative mt-1 w-full bg-[#202124] rounded-lg shadow-lg border border-gray-700 z-10 p-2"
          >
            <h4 className="px-2 py-1 text-xs font-semibold text-gray-400">BACKGROUNDS</h4>
            <div className="grid grid-cols-3 gap-2 p-2">
              {backgrounds.map((bg) => (
                <button
                  key={bg.id}
                  className={`flex flex-col items-center p-1 rounded ${
                    selectedBackground === bg.id ? 'bg-[#3c4043]' : 'hover:bg-[#2d2f31]'
                  }`}
                  onClick={() => handleBackgroundSelect(bg.id)}
                >
                  <div className="w-16 h-16 bg-gray-600 rounded mb-1 flex items-center justify-center text-xs">
                    {bg.icon}
                  </div>
                  <span className="text-xs">{bg.name}</span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
      
      <div className="border-t border-gray-700"></div>
      
      {/* Other options */}
      <button className="w-full text-left px-4 py-2 text-sm hover:bg-[#3c4043] flex items-center" onClick={onReportProblem}>
        <FaExclamationCircle className="mr-3 text-gray-400" fontSize="small" />
        Report a problem
      </button>
      <button className="w-full text-left px-4 py-2 text-sm hover:bg-[#3c4043] flex items-center" onClick={onReportAbuse}>
        <FaFlag className="mr-3 text-gray-400" fontSize="small" />
        Report abuse
      </button>
      <button className="w-full text-left px-4 py-2 text-sm hover:bg-[#3c4043] flex items-center" onClick={onHelp}>
        <FaQuestionCircle className="mr-3 text-gray-400" fontSize="small" />
        Troubleshooting & help
      </button>
      <button className="w-full text-left px-4 py-2 text-sm hover:bg-[#3c4043] flex items-center" onClick={onSettings}>
        <FaCog className="mr-3 text-gray-400" fontSize="small" />
        Settings
      </button>
    </div>
  );
};

ShowMore.propTypes = {
  onClose: PropTypes.func,
  onFullScreen: PropTypes.func,
  onPiP: PropTypes.func,
  onReportProblem: PropTypes.func,
  onReportAbuse: PropTypes.func,
  onHelp: PropTypes.func,
  onSettings: PropTypes.func,
  onAdjustView: PropTypes.func,
  onBackgroundEffects: PropTypes.func,
  initialRecordingAllowed: PropTypes.bool,
  initialBackground: PropTypes.string,
  initialView: PropTypes.string,
};

export default ShowMore;